import React, { useEffect } from 'react';
import Form, { FormInstance } from 'antd/lib/form';

import { Col, InputNumber, Row } from 'antd';
import { ClientFeatureConfig } from '../../../types/Feature';
import FormListAddButton from '../../FormListAddButton';
import PriceMatrixServiceInput from './PriceMatrixServiceInput';
import { PriceMatrixAttributes } from '../../../../../server/src/models';
import PricePerItemList from './PricePerItemList';
import FeesSection from './FeesSection';

const cardHeaderTitleStyle = { fontSize: '18px' };

interface PriceMatrixFormSectionProps {
    isDetailMode: boolean;
    formInstance: FormInstance;
    priceMatrixConfig?: ClientFeatureConfig<PriceMatrixAttributes>;
}

const TaxSection = ({ taxValue, isDetailMode }: { taxValue: number | undefined; isDetailMode: boolean }) => {
    return (
        <>
            <div className="section-tax-value card-header border-bottom d-flex justify-content-between align-items-center bg-white">
                <p className="m-0 p-0 h5" style={cardHeaderTitleStyle}>
                    Tax value
                </p>
            </div>
            <div className="card-body">
                <Row gutter={24}>
                    <Col span={12}>
                        <Form.Item name="taxValue" label="Tax percentage (%)" colon labelCol={{ span: 12 }}>
                            {isDetailMode ? (
                                <span>{taxValue}</span>
                            ) : (
                                <InputNumber
                                    max={100}
                                    min={0}
                                    title="Tax percentage. For example, VAT is 20% in France"
                                />
                            )}
                        </Form.Item>
                    </Col>
                </Row>
            </div>
        </>
    );
};

const PriceMatrixFormSection: React.FC<PriceMatrixFormSectionProps> = ({
    formInstance,
    priceMatrixConfig,
    isDetailMode,
}: PriceMatrixFormSectionProps) => {
    useEffect(() => {
        if (priceMatrixConfig) {
            formInstance.setFields([
                {
                    name: 'priceMatrix',
                    value: priceMatrixConfig?.config?.priceMatrix,
                },
                {
                    name: 'distanceFees',
                    value: priceMatrixConfig?.config?.distanceFees,
                },
                {
                    name: 'dateFees',
                    value: priceMatrixConfig?.config?.dateFees,
                },
                {
                    name: 'serviceFees',
                    value: priceMatrixConfig?.config?.serviceFees,
                },
                {
                    name: 'pricePerItem',
                    value: priceMatrixConfig?.config?.pricePerItem,
                },
                {
                    name: 'taxValue',
                    value: priceMatrixConfig?.config?.taxValue,
                },
            ]);
        }
    }, [priceMatrixConfig]);

    return (
        <div className="card border-0" id="price-matrix-section">
            <TaxSection taxValue={priceMatrixConfig?.config?.taxValue} isDetailMode={isDetailMode} />
            <FeesSection isDetailMode={isDetailMode} priceMatrixConfig={priceMatrixConfig} />
            <div className="section-price-matrices card-header border-bottom d-flex justify-content-between align-items-center bg-white">
                <p className="m-0 p-0 h5" style={cardHeaderTitleStyle}>
                    Price Matrices
                </p>
            </div>
            <div className="card-body">
                <Row gutter={24}>
                    <Col span={24}>
                        <Form.List name="priceMatrix">
                            {(services, { add: newService, remove: removeService }): JSX.Element => (
                                <>
                                    {services.map((service) => (
                                        <PriceMatrixServiceInput
                                            key={service.key}
                                            isDetailMode={isDetailMode}
                                            serviceMatrix={priceMatrixConfig?.config.priceMatrix[service.key]}
                                            fieldName={service.name}
                                            remove={removeService}
                                        />
                                    ))}
                                    <FormListAddButton
                                        id="add-price-matrix-service"
                                        add={newService}
                                        isDetailMode={isDetailMode}
                                        text="Add a service price matrix"
                                    />
                                </>
                            )}
                        </Form.List>
                    </Col>
                </Row>
            </div>
            <PricePerItemList pricesPerItem={priceMatrixConfig?.config.pricePerItem} isDetailMode={isDetailMode} />
        </div>
    );
};

export default PriceMatrixFormSection;
